/**
 * Runtime client configuration.
 *
 * The split apps ship ONE environment-agnostic bundle (build-once, promoted
 * staging -> prod). Environment-specific values (Sentry DSN + environment,
 * publishable client keys, base domain) are therefore NOT baked by Vite — the
 * Laravel shell injects them per request into the #app-config JSON island
 * (config/client.php + resources/views/app.blade.php), and this module reads
 * them once at load.
 *
 * Fallbacks keep it safe outside the shell (unit tests, SSR): a missing island
 * yields empty values, and the base domain falls back to the current hostname's
 * registrable base so links still resolve.
 *
 * NOTE: build-IDENTITY values (e.g. VITE_RELEASE) intentionally stay on
 * import.meta.env — they describe the artifact, not the deployment.
 */

export interface ClientConfig {
  /** Canonical environment name (APP_ENV): "local" | "staging" | "production". */
  appEnv: string;
  /** Shared base domain for building tenant / deep-link URLs (e.g. "dms44.com"). */
  baseDomain: string;
  /** Sentry BROWSER DSN ("" disables). Distinct from the server Sentry DSN. */
  sentryDsn: string;
  /** Publishable weatherapi.com key. */
  weatherApiKey: string;
  /** Syncfusion community/commercial license key. */
  syncfusionLicense: string;
}

function readIsland(): Record<string, unknown> {
  if (typeof document === 'undefined') return {};
  const text = document.getElementById('app-config')?.textContent?.trim();
  if (!text) return {};
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

/** Last-resort base domain from the current host (stws.dms44.com -> dms44.com). */
function deriveBaseDomain(): string {
  if (typeof window === 'undefined') return '';
  const labels = window.location.hostname.split('.');
  return labels.length >= 2 ? labels.slice(-2).join('.') : labels.join('.');
}

const raw = readIsland();

export const config: ClientConfig = {
  appEnv: str(raw.app_env) || 'production',
  baseDomain: str(raw.base_domain) || deriveBaseDomain(),
  sentryDsn: str(raw.sentry_dsn),
  weatherApiKey: str(raw.weather_api_key),
  syncfusionLicense: str(raw.syncfusion_license),
};
